import React, { useCallback, useEffect, useRef, useState } from "react";
import { useAuthState } from "react-firebase-hooks/auth";
import { getIdTokenResult } from "firebase/auth";
import { httpsCallable } from "firebase/functions";
import { auth, functions } from "../config/firebase";
import { canAccessAdminPortal } from "../utils/adminRoles";
import "./RecoveryOperationsControls.css";

const LIMIT = 25;
const QUEUES = { refund: "Refund recovery", outbox: "Fulfillment outbox" };
const STATUSES = ["needs_review", "pending", "running", "completed"];
const validId = (value) => typeof value === "string" && /^[A-Za-z0-9_-]{1,150}$/.test(value);
const validNumber = (value) => Number.isSafeInteger(value) && value >= 0;
const nullableNumber = (value) => value === null || validNumber(value);
const label = (value) => value.replace(/_/g, " ");
const date = (value) => validNumber(value) && value <= 8640000000000000 ? new Date(value).toISOString() : "Not recorded";

function validatePage(data, scope, afterId) {
	if (!data || ["firebaseProject", "restaurantId", "queue", "status"].some((key) => data[key] !== scope[key]) ||
		!validId(data.auditId) || !validNumber(data.observedAtMs) || !Array.isArray(data.records) || data.records.length > LIMIT) return false;
	let previous = afterId || "";
	for (const row of data.records) {
		if (!row || !validId(row.id) || row.id <= previous || row.restaurantId !== scope.restaurantId || row.status !== scope.status ||
			(row.orderId !== null && !validId(row.orderId)) || !nullableNumber(row.attempts) ||
			!["consistent", "needs_review"].includes(row.identityStatus) ||
			!(scope.queue === "refund" ? ["refund"] : ["paid_pickup", "paid_rewards", "separate_transfer", "unknown"]).includes(row.kind) ||
			!["active", "expired", "none", "unknown"].includes(row.leaseState) ||
			!["paused", "worker_managed", "unknown", "scheduled", "due"].includes(row.scheduleState) ||
			!["pending", "succeeded", "failed", "canceled", "unknown"].includes(row.lastOutcome) ||
			!["handler_failed", "balance_insufficient", "disputed_payment", "external_adjustment", "unknown"].includes(row.lastFailureCode) ||
			![row.createdAtMs, row.updatedAtMs, row.completedAtMs].every(nullableNumber) ||
			!row.alert || !["missing", "scope_mismatch", "open", "resolved", "unknown"].includes(row.alert.status) ||
			!["not_started", "sending", "sent", "needs_review", "unknown"].includes(row.alert.emailStatus)) return false;
		previous = row.id;
	}
	return data.nextAfterId === null || (data.records.length === LIMIT && validId(data.nextAfterId) && data.nextAfterId === previous);
}

const errorText = (error) => {
	const code = String(error?.code || "").replace(/^functions\//, "");
	if (["unauthenticated", "permission-denied"].includes(code)) return "Scerv admin access is required. Sign in with an authorized support account.";
	if (code === "failed-precondition") return "Recovery scope or query configuration requires support review.";
	if (["not-found", "unimplemented"].includes(code)) return "Recovery diagnostics endpoint is unavailable.";
	return "Could not load recovery diagnostics. Reload to try again.";
};

function OperationsQueue({ restaurantId, projectId, user }) {
	const [queue, setQueue] = useState("refund");
	const [status, setStatus] = useState("needs_review");
	const [cursors, setCursors] = useState([null]);
	const [result, setResult] = useState(null);
	const [busy, setBusy] = useState(true);
	const [error, setError] = useState("");
	const generation = useRef(0);
	const alive = useRef(false);
	const pending = useRef(false);
	const afterId = cursors[cursors.length - 1];
	const scopeKey = `${projectId}:${restaurantId}:${queue}:${status}:${afterId || ""}`;
	const current = useCallback((ticket) => alive.current && ticket === generation.current && auth.currentUser === user &&
		functions.app.options.projectId === projectId, [user, projectId]);
	const reload = useCallback(async () => {
		if (pending.current || auth.currentUser !== user) return;
		const ticket = ++generation.current;
		pending.current = true;
		setBusy(true); setResult(null); setError("");
		try {
			const token = await getIdTokenResult(user);
			if (!current(ticket)) return;
			const role = String(token.claims.role || "");
			if (!canAccessAdminPortal(role) || role !== role.trim()) throw Object.assign(new Error("Denied"), { code: "permission-denied" });
			const scope = { firebaseProject: projectId, restaurantId, queue, status };
			const response = await httpsCallable(functions, "listScervRecoveryOperations")({ ...scope, limit: LIMIT, ...(afterId ? { afterId } : {}) });
			if (!current(ticket)) return;
			if (!validatePage(response.data, scope, afterId)) throw new Error("Invalid recovery diagnostics response");
			setResult({ data: response.data, scopeKey, user });
		} catch (err) {
			if (current(ticket)) setError(errorText(err));
		} finally {
			if (current(ticket)) { pending.current = false; setBusy(false); }
		}
	}, [afterId, current, projectId, queue, restaurantId, scopeKey, status, user]);
	useEffect(() => {
		// generation is a request counter, not a DOM ref; bump the same ref object on cleanup.
		const requests = generation;
		alive.current = true;
		reload();
		return () => { alive.current = false; requests.current++; pending.current = false; };
	}, [reload]);
	const page = result?.scopeKey === scopeKey && result.user === user ? result.data : null;
	return <section className="recovery-operations-controls" aria-labelledby="recovery-operations-heading" aria-busy={busy}>
		<header><h2 id="recovery-operations-heading">Recovery operations</h2>
			<button type="button" aria-label="Reload recovery diagnostics" title="Reload recovery diagnostics" disabled={busy} onClick={reload}>&#8635;</button>
		</header>
		<div className="recovery-operations-filters">
			<label>Queue<select value={queue} onChange={(event) => { setQueue(event.target.value); setCursors([null]); }}>
				{Object.entries(QUEUES).map(([value, text]) => <option key={value} value={value}>{text}</option>)}
			</select></label>
			<label>Status<select value={status} onChange={(event) => { setStatus(event.target.value); setCursors([null]); }}>
				{STATUSES.map((value) => <option key={value} value={value}>{label(value)}</option>)}
			</select></label>
		</div>
		<p className="recovery-operations-scope">{projectId} / {restaurantId}</p>
		{error && <p role="alert" className="restaurant-error">{error}</p>}
		{busy && <p role="status">Loading recovery diagnostics...</p>}
		{page && <>
			<p role="status">{page.records.length} records | Observed {date(page.observedAtMs)}</p>
			{page.records.length === 0 ? <p>No matching recovery jobs.</p> : <div className="recovery-operations-scroll" tabIndex="0" role="region" aria-label="Recovery jobs">
				<table><thead><tr>{["Job / order", "Effect / identity", "Attempts / outcome", "Schedule / lease", "Alert / email", "Updated"].map((text) => <th key={text} scope="col">{text}</th>)}</tr></thead>
					<tbody>{page.records.map((row) => <tr key={row.id}>
						<td><span>{row.id}</span><span>{row.orderId || "Order not recorded"}</span></td>
						<td><span>{label(row.kind)}</span><span>{label(row.identityStatus)}</span></td>
						<td><span>{row.attempts === null ? "Not recorded" : row.attempts}</span><span>{label(row.lastOutcome)}</span><span>{label(row.lastFailureCode)}</span></td>
						<td><span>{label(row.scheduleState)}</span><span>{label(row.leaseState)}</span></td>
						<td><span>{label(row.alert.status)}</span><span>{label(row.alert.emailStatus)}</span></td>
						<td>{date(row.updatedAtMs ?? row.completedAtMs ?? row.createdAtMs)}</td>
					</tr>)}</tbody>
				</table>
			</div>}
			<p className="recovery-operations-audit">Audit: {page.auditId}</p>
		</>}
		<nav aria-label="Recovery pages">
			<button type="button" aria-label="Previous recovery page" title="Previous recovery page" disabled={busy || cursors.length === 1} onClick={() => setCursors((values) => values.slice(0, -1))}>&#8592;</button>
			<span>Page {cursors.length}</span>
			<button type="button" aria-label="Next recovery page" title="Next recovery page" disabled={busy || !page?.nextAfterId} onClick={() => setCursors((values) => [...values, page.nextAfterId])}>&#8594;</button>
		</nav>
	</section>;
}

export default function RecoveryOperationsControls({ restaurantId }) {
	const [user, loading, error] = useAuthState(auth);
	const projectId = functions.app?.options?.projectId;
	if (loading) return <p role="status">Checking support access...</p>;
	if (error || !user || auth.currentUser !== user) return <p role="alert">Sign in with an authorized support account.</p>;
	if (!validId(restaurantId) || !validId(projectId)) return <p role="alert">A valid restaurant and project are required.</p>;
	return <OperationsQueue key={`${projectId}:${restaurantId}:${user.uid}`} restaurantId={restaurantId} projectId={projectId} user={user} />;
}
